import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import { auth, db } from "../firebase";

function ManageLeads() {
  const navigate = useNavigate();

  const emptyForm = {
    customerName: "",
    phone: "",
    alternatePhone: "",
    email: "",
    source: "",
    requirement: "",
    priority: "Normal",
    assignedEmployeeId: "",
    deadline: "",
    adminNote: "",
  };

  const [employees, setEmployees] =
    useState([]);

  const [leads, setLeads] =
    useState([]);

  const [formData, setFormData] =
    useState(emptyForm);

  const [
    editingLeadId,
    setEditingLeadId,
  ] = useState("");

  const [
    searchText,
    setSearchText,
  ] = useState("");

  const [
    employeeFilter,
    setEmployeeFilter,
  ] = useState("All");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("All");

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [
    bulkUploading,
    setBulkUploading,
  ] = useState(false);

  const loadPageData =
    useCallback(
      async () => {
        try {
          setLoading(true);

          const [
            employeesSnapshot,
            leadsSnapshot,
          ] = await Promise.all([
            getDocs(
              collection(
                db,
                "employees"
              )
            ),

            getDocs(
              collection(
                db,
                "leads"
              )
            ),
          ]);

          const employeeList =
            employeesSnapshot.docs.map(
              (
                employeeDocument
              ) => ({
                id:
                  employeeDocument.id,

                ...employeeDocument.data(),
              })
            );

          const leadList =
            leadsSnapshot.docs.map(
              (
                leadDocument
              ) => ({
                id:
                  leadDocument.id,

                ...leadDocument.data(),
              })
            );

          employeeList.sort(
            (
              first,
              second
            ) =>
              (
                first.name ||
                ""
              ).localeCompare(
                second.name ||
                  ""
              )
          );

          leadList.sort(
            (
              first,
              second
            ) => {
              const firstDate =
                first.createdAt
                  ?.toDate?.()
                  ?.getTime?.() ||
                0;

              const secondDate =
                second.createdAt
                  ?.toDate?.()
                  ?.getTime?.() ||
                0;

              return (
                secondDate -
                firstDate
              );
            }
          );

          setEmployees(
            employeeList
          );

          setLeads(
            leadList
          );
        } catch (error) {
          console.error(
            "Load leads error:",
            error
          );

          setMessage(
            "Unable to load leads."
          );
        } finally {
          setLoading(false);
        }
      },
      []
    );

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const handleInputChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;

    setFormData(
      (
        currentData
      ) => ({
        ...currentData,
        [name]: value,
      })
    );
  };

  const getEmployeeName = (
    employeeId
  ) => {
    const foundEmployee =
      employees.find(
        (
          employeeItem
        ) =>
          employeeItem.id ===
          employeeId
      );

    return (
      foundEmployee?.name ||
      "Unassigned"
    );
  };

  const getEmployeeEmail = (
    employeeId
  ) => {
    const foundEmployee =
      employees.find(
        (
          employeeItem
        ) =>
          employeeItem.id ===
          employeeId
      );

    return (
      foundEmployee?.email ||
      ""
    );
  };

  const resetForm = () => {
    setFormData(
      emptyForm
    );

    setEditingLeadId(
      ""
    );
  };

  const buildAdminEditHistory =
    (
      oldLead,
      newData,
      selectedEmployee
    ) => {
      const fields = [
        {
          key:
            "customerName",

          label:
            "Customer Name",

          before:
            oldLead.customerName ||
            oldLead.name ||
            "",

          after:
            newData.customerName,
        },

        {
          key:
            "phone",

          label:
            "Primary Number",

          before:
            oldLead.phone ||
            "",

          after:
            newData.phone,
        },

        {
          key:
            "alternatePhone",

          label:
            "Alternate Number",

          before:
            oldLead.alternatePhone ||
            oldLead.alternateNumber ||
            "",

          after:
            newData.alternatePhone,
        },

        {
          key:
            "email",

          label:
            "Email",

          before:
            oldLead.email ||
            "",

          after:
            newData.email,
        },

        {
          key:
            "source",

          label:
            "Lead Source",

          before:
            oldLead.source ||
            "",

          after:
            newData.source,
        },

        {
          key:
            "requirement",

          label:
            "Customer Requirement",

          before:
            oldLead.requirement ||
            "",

          after:
            newData.requirement,
        },

        {
          key:
            "priority",

          label:
            "Priority",

          before:
            oldLead.priority ||
            "Normal",

          after:
            newData.priority,
        },

        {
          key:
            "deadline",

          label:
            "Deadline",

          before:
            oldLead.deadline ||
            "",

          after:
            newData.deadline,
        },

        {
          key:
            "assignedEmployee",

          label:
            "Assigned Employee",

          before:
            oldLead.assignedEmployeeName ||
            getEmployeeName(
              oldLead.assignedEmployeeId
            ) ||
            "",

          after:
            selectedEmployee?.name ||
            "",
        },
      ];

      const changes =
        fields
          .filter(
            (field) =>
              String(
                field.before ??
                ""
              ) !==
              String(
                field.after ??
                ""
              )
          )
          .map(
            (field) => ({
              field:
                field.key,

              label:
                field.label,

              before:
                String(
                  field.before ??
                  ""
                ),

              after:
                String(
                  field.after ??
                  ""
                ),
            })
          );

      if (
        changes.length ===
        0
      ) {
        return null;
      }

      return {
        changedById:
          auth.currentUser
            ?.uid ||
          "",

        changedByName:
          "Admin",

        changedByEmail:
          auth.currentUser
            ?.email ||
          "",

        changedByRole:
          "Admin",

        changedAt:
          new Date()
            .toISOString(),

        changes,
      };
    };

  const handleSaveLead =
    async (
      event
    ) => {
      event.preventDefault();

      if (
        !formData.customerName.trim()
      ) {
        setMessage(
          "Customer name is required."
        );

        return;
      }

      if (
        !formData.phone.trim()
      ) {
        setMessage(
          "Phone number is required."
        );

        return;
      }

      if (
        !formData.assignedEmployeeId
      ) {
        setMessage(
          "Please assign the lead to an employee."
        );

        return;
      }

      try {
        setSaving(true);
        setMessage("");

        const selectedEmployee =
          employees.find(
            (
              employeeItem
            ) =>
              employeeItem.id ===
              formData.assignedEmployeeId
          );

        if (
          !selectedEmployee
        ) {
          setMessage(
            "Selected employee was not found."
          );

          return;
        }

        const cleanData = {
          customerName:
            formData.customerName.trim(),

          phone:
            formData.phone.trim(),

          alternatePhone:
            formData.alternatePhone.trim(),

          email:
            formData.email.trim(),

          source:
            formData.source.trim(),

          requirement:
            formData.requirement.trim(),

          priority:
            formData.priority,

          deadline:
            formData.deadline,

          adminNote:
            formData.adminNote.trim(),
        };

        const leadData = {
          customerName:
            cleanData.customerName,

          name:
            cleanData.customerName,

          phone:
            cleanData.phone,

          alternatePhone:
            cleanData.alternatePhone,

          email:
            cleanData.email,

          source:
            cleanData.source,

          requirement:
            cleanData.requirement,

          priority:
            cleanData.priority,

          assignedEmployeeId:
            selectedEmployee.id,

          assignedEmployeeName:
            selectedEmployee.name ||
            "",

          assignedEmployeeEmail:
            (
              selectedEmployee.email ||
              ""
            )
              .trim()
              .toLowerCase(),

          deadline:
            cleanData.deadline,

          adminNote:
            cleanData.adminNote,

          updatedAt:
            serverTimestamp(),
        };

        if (
          editingLeadId
        ) {
          const oldLead =
            leads.find(
              (lead) =>
                lead.id ===
                editingLeadId
            );

          if (!oldLead) {
            setMessage(
              "Lead record was not found."
            );

            return;
          }

          const editHistoryEntry =
            buildAdminEditHistory(
              oldLead,
              cleanData,
              selectedEmployee
            );

          const updateData = {
            ...leadData,
          };

          if (
            editHistoryEntry
          ) {
            updateData.leadEditHistory =
              arrayUnion(
                editHistoryEntry
              );
          }

          /*
            Private Admin Note is intentionally
            NOT placed in leadEditHistory because
            employees can see leadEditHistory.

            The note itself remains private exactly
            as before.
          */

          await updateDoc(
            doc(
              db,
              "leads",
              editingLeadId
            ),
            updateData
          );

          /*
            If assignment was changed through
            this Edit Lead form, also preserve the
            existing reassignment/churn history.
          */
          if (
            oldLead.assignedEmployeeId &&
            oldLead.assignedEmployeeId !==
              selectedEmployee.id
          ) {
            const reassignmentEntry = {
              fromEmployeeId:
                oldLead.assignedEmployeeId ||
                "",

              fromEmployeeName:
                oldLead.assignedEmployeeName ||
                getEmployeeName(
                  oldLead.assignedEmployeeId
                ),

              fromEmployeeEmail:
                oldLead.assignedEmployeeEmail ||
                getEmployeeEmail(
                  oldLead.assignedEmployeeId
                ),

              toEmployeeId:
                selectedEmployee.id,

              toEmployeeName:
                selectedEmployee.name ||
                "",

              toEmployeeEmail:
                selectedEmployee.email ||
                "",

              changedAt:
                new Date()
                  .toISOString(),

              changedBy:
                "Admin",
            };

            await updateDoc(
              doc(
                db,
                "leads",
                editingLeadId
              ),
              {
                reassignmentHistory:
                  arrayUnion(
                    reassignmentEntry
                  ),
              }
            );
          }

          if (
            editHistoryEntry
          ) {
            setMessage(
              "Lead updated successfully. Changes recorded in edit history."
            );
          } else {
            setMessage(
              "Lead saved. No customer detail changes were detected."
            );
          }
        } else {
          await addDoc(
            collection(
              db,
              "leads"
            ),
            {
              ...leadData,

              status:
                "Pending",

              callbackStatus:
                "",

              remarksHistory:
                [],

              reassignmentHistory:
                [],

              leadEditHistory:
                [],

              createdAt:
                serverTimestamp(),
            }
          );

          setMessage(
            "Lead added successfully."
          );
        }

        resetForm();

        await loadPageData();
      } catch (error) {
        console.error(
          "Save lead error:",
          error
        );

        setMessage(
          `Unable to save lead: ${
            error.message ||
            "Firebase error"
          }`
        );
      } finally {
        setSaving(false);
      }
    };

  const handleEditLead = (
    lead
  ) => {
    setEditingLeadId(
      lead.id
    );

    setFormData({
      customerName:
        lead.customerName ||
        lead.name ||
        "",

      phone:
        lead.phone ||
        "",

      alternatePhone:
        lead.alternatePhone ||
        lead.alternateNumber ||
        "",

      email:
        lead.email ||
        "",

      source:
        lead.source ||
        "",

      requirement:
        lead.requirement ||
        "",

      priority:
        lead.priority ||
        "Normal",

      assignedEmployeeId:
        lead.assignedEmployeeId ||
        "",

      deadline:
        lead.deadline ||
        "",

      adminNote:
        lead.adminNote ||
        "",
    });

    setMessage("");

    window.scrollTo({
      top: 0,
      behavior:
        "smooth",
    });
  };

  const handleDeleteLead =
    async (
      leadId
    ) => {
      const shouldDelete =
        window.confirm(
          "Delete this lead permanently?"
        );

      if (
        !shouldDelete
      ) {
        return;
      }

      try {
        await deleteDoc(
          doc(
            db,
            "leads",
            leadId
          )
        );

        setMessage(
          "Lead deleted successfully."
        );

        await loadPageData();
      } catch (error) {
        console.error(
          "Delete lead error:",
          error
        );

        setMessage(
          "Unable to delete lead."
        );
      }
    };

  const handleReassignLead =
    async (
      lead,
      newEmployeeId
    ) => {
      if (
        !newEmployeeId
      ) {
        return;
      }

      if (
        newEmployeeId ===
        lead.assignedEmployeeId
      ) {
        return;
      }

      const newEmployee =
        employees.find(
          (
            employeeItem
          ) =>
            employeeItem.id ===
            newEmployeeId
        );

      if (
        !newEmployee
      ) {
        setMessage(
          "Selected employee was not found."
        );

        return;
      }

      const customerName =
        lead.customerName ||
        lead.name ||
        "this customer";

      const shouldReassign =
        window.confirm(
          `Reassign ${customerName} to ${newEmployee.name}?`
        );

      if (
        !shouldReassign
      ) {
        return;
      }

      try {
        const changedAt =
          new Date()
            .toISOString();

        const reassignmentEntry = {
          fromEmployeeId:
            lead.assignedEmployeeId ||
            "",

          fromEmployeeName:
            lead.assignedEmployeeName ||
            getEmployeeName(
              lead.assignedEmployeeId
            ),

          fromEmployeeEmail:
            lead.assignedEmployeeEmail ||
            "",

          toEmployeeId:
            newEmployee.id,

          toEmployeeName:
            newEmployee.name ||
            "",

          toEmployeeEmail:
            newEmployee.email ||
            "",

          changedAt,

          changedBy:
            "Admin",
        };

        const editHistoryEntry = {
          changedById:
            auth.currentUser
              ?.uid ||
            "",

          changedByName:
            "Admin",

          changedByEmail:
            auth.currentUser
              ?.email ||
            "",

          changedByRole:
            "Admin",

          changedAt,

          changes: [
            {
              field:
                "assignedEmployee",

              label:
                "Assigned Employee",

              before:
                lead.assignedEmployeeName ||
                getEmployeeName(
                  lead.assignedEmployeeId
                ) ||
                "",

              after:
                newEmployee.name ||
                "",
            },
          ],
        };

        await updateDoc(
          doc(
            db,
            "leads",
            lead.id
          ),
          {
            assignedEmployeeId:
              newEmployee.id,

            assignedEmployeeName:
              newEmployee.name ||
              "",

            assignedEmployeeEmail:
              (
                newEmployee.email ||
                ""
              )
                .trim()
                .toLowerCase(),

            reassignmentHistory:
              arrayUnion(
                reassignmentEntry
              ),

            leadEditHistory:
              arrayUnion(
                editHistoryEntry
              ),

            updatedAt:
              serverTimestamp(),
          }
        );

        setMessage(
          `Lead reassigned to ${newEmployee.name}.`
        );

        await loadPageData();
      } catch (error) {
        console.error(
          "Reassign lead error:",
          error
        );

        setMessage(
          "Unable to reassign lead."
        );
      }
    };

  const parseCsvLine = (
    line
  ) => {
    const values = [];

    let currentValue =
      "";

    let insideQuotes =
      false;

    for (
      let index = 0;
      index < line.length;
      index += 1
    ) {
      const character =
        line[index];

      if (
        character === '"'
      ) {
        if (
          insideQuotes &&
          line[
            index + 1
          ] === '"'
        ) {
          currentValue +=
            '"';

          index += 1;
        } else {
          insideQuotes =
            !insideQuotes;
        }
      } else if (
        character === "," &&
        !insideQuotes
      ) {
        values.push(
          currentValue.trim()
        );

        currentValue =
          "";
      } else {
        currentValue +=
          character;
      }
    }

    values.push(
      currentValue.trim()
    );

    return values.map(
      (value) =>
        value
          .replace(
            /^"|"$/g,
            ""
          )
          .trim()
    );
  };

  const normalizeHeader = (
    header
  ) =>
    header
      .trim()
      .toLowerCase()
      .replace(
        /\s+/g,
        ""
      )
      .replace(
        /[_-]/g,
        ""
      );

  const normalizePhone = (
    phoneValue
  ) => {
    if (!phoneValue) {
      return "";
    }

    let cleaned =
      String(
        phoneValue
      )
        .trim()
        .replace(
          /\s+/g,
          ""
        )
        .replace(
          /[^\d+]/g,
          ""
        );

    if (
      cleaned.startsWith(
        "+91"
      )
    ) {
      return cleaned;
    }

    if (
      cleaned.startsWith(
        "91"
      ) &&
      cleaned.length ===
        12
    ) {
      return `+${cleaned}`;
    }

    if (
      cleaned.length ===
      10
    ) {
      return `+91${cleaned}`;
    }

    return cleaned;
  };

  const detectStatusFromRemark =
    (
      remarkValue
    ) => {
      const remark =
        (
          remarkValue ||
          ""
        )
          .trim()
          .toLowerCase();

      if (!remark) {
        return "Pending";
      }

      if (
        remark.includes(
          "not interested"
        ) ||
        remark.includes(
          "not intrested"
        )
      ) {
        return "Not Interested";
      }

      if (
        remark.includes(
          "busy"
        )
      ) {
        return "Busy";
      }

      if (
        remark.includes(
          "ringing"
        )
      ) {
        return "Ringing";
      }

      if (
        remark.includes(
          "switched off"
        ) ||
        remark.includes(
          "switch off"
        )
      ) {
        return "Switched Off";
      }

      if (
        remark.includes(
          "incoming not available"
        ) ||
        remark.includes(
          "not available"
        )
      ) {
        return "Not Answered";
      }

      if (
        remark.includes(
          "hang up"
        ) ||
        remark.includes(
          "hung up"
        ) ||
        remark.includes(
          "direct hang"
        )
      ) {
        return "Hang Up";
      }

      if (
        remark === "nc" ||
        remark.includes(
          "not connected"
        )
      ) {
        return "Not Answered";
      }

      return "Pending";
    };

  const handleBulkUpload =
    async (
      event
    ) => {
      const selectedFile =
        event.target
          .files?.[0];

      event.target.value =
        "";

      if (
        !selectedFile
      ) {
        return;
      }

      if (
        !selectedFile.name
          .toLowerCase()
          .endsWith(
            ".csv"
          )
      ) {
        setMessage(
          "Please upload a CSV file."
        );

        return;
      }

      try {
        setBulkUploading(
          true
        );

        setMessage("");

        const fileText =
          await selectedFile.text();

        const rows =
          fileText
            .split(
              /\r?\n/
            )
            .filter(
              (row) =>
                row.trim()
            );

        if (
          rows.length <
          2
        ) {
          setMessage(
            "CSV file has no customer rows."
          );

          return;
        }

        const headers =
          parseCsvLine(
            rows[0]
          ).map(
            normalizeHeader
          );

        const getColumnValue =
          (
            rowValues,
            possibleHeaders
          ) => {
            const headerIndex =
              headers.findIndex(
                (header) =>
                  possibleHeaders.includes(
                    header
                  )
              );

            if (
              headerIndex ===
              -1
            ) {
              return "";
            }

            return (
              rowValues[
                headerIndex
              ] ||
              ""
            );
          };

        let uploadedCount =
          0;

        let skippedCount =
          0;

        for (
          let rowIndex = 1;
          rowIndex <
          rows.length;
          rowIndex += 1
        ) {
          const rowValues =
            parseCsvLine(
              rows[
                rowIndex
              ]
            );

          const customerName =
            getColumnValue(
              rowValues,
              [
                "customername",
                "name",
                "clientname",
              ]
            );

          const phoneRaw =
            getColumnValue(
              rowValues,
              [
                "customernumber",
                "number",
                "phone",
                "phonenumber",
                "mobile",
                "mobilenumber",
                "contact",
              ]
            );

          const phone =
            normalizePhone(
              phoneRaw
            );

          const alternatePhoneRaw =
            getColumnValue(
              rowValues,
              [
                "alternatenumber",
                "alternatephone",
                "alternate",
                "secondarynumber",
                "secondaryphone",
              ]
            );

          const alternatePhone =
            normalizePhone(
              alternatePhoneRaw
            );

          const employeeEmail =
            getColumnValue(
              rowValues,
              [
                "employeeemail",
                "assignedemployeeemail",
                "assignedemail",
              ]
            ).toLowerCase();

          const employeeName =
            getColumnValue(
              rowValues,
              [
                "employeename",
                "employee",
                "assignedemployee",
                "assignedemployeename",
              ]
            ).toLowerCase();

          const oldRemark =
            getColumnValue(
              rowValues,
              [
                "remark",
                "remarks",
                "remark1",
                "previousremark",
                "oldremark",
                "note",
              ]
            );

          let assignedEmployee =
            null;

          if (
            employeeEmail
          ) {
            assignedEmployee =
              employees.find(
                (
                  employeeItem
                ) =>
                  (
                    employeeItem.email ||
                    ""
                  ).toLowerCase() ===
                  employeeEmail
              );
          }

          if (
            !assignedEmployee &&
            employeeName
          ) {
            assignedEmployee =
              employees.find(
                (
                  employeeItem
                ) =>
                  (
                    employeeItem.name ||
                    ""
                  ).toLowerCase() ===
                  employeeName
              );
          }

          if (
            !assignedEmployee &&
            formData.assignedEmployeeId
          ) {
            assignedEmployee =
              employees.find(
                (
                  employeeItem
                ) =>
                  employeeItem.id ===
                  formData.assignedEmployeeId
              );
          }

          if (
            !customerName ||
            !phone ||
            !assignedEmployee
          ) {
            skippedCount +=
              1;

            continue;
          }

          const importedHistory =
            oldRemark
              ? [
                  {
                    employeeId:
                      assignedEmployee.id ||
                      "",

                    employeeName:
                      assignedEmployee.name ||
                      "",

                    employeeEmail:
                      assignedEmployee.email ||
                      "",

                    connectionType:
                      "Imported",

                    disposition:
                      detectStatusFromRemark(
                        oldRemark
                      ),

                    discussion:
                      oldRemark,

                    remark:
                      oldRemark,

                    source:
                      "Imported Previous Remark",

                    imported:
                      true,

                    createdAt:
                      new Date()
                        .toISOString(),
                  },
                ]
              : [];

          await addDoc(
            collection(
              db,
              "leads"
            ),
            {
              customerName,

              name:
                customerName,

              phone,

              alternatePhone,

              email:
                getColumnValue(
                  rowValues,
                  [
                    "email",
                    "customeremail",
                  ]
                ),

              source:
                getColumnValue(
                  rowValues,
                  [
                    "source",
                    "leadsource",
                  ]
                ),

              requirement:
                getColumnValue(
                  rowValues,
                  [
                    "requirement",
                    "customerrequirement",
                    "details",
                  ]
                ),

              priority:
                getColumnValue(
                  rowValues,
                  [
                    "priority",
                  ]
                ) ||
                "Normal",

              deadline:
                getColumnValue(
                  rowValues,
                  [
                    "deadline",
                    "duedate",
                    "date",
                  ]
                ),

              assignedEmployeeId:
                assignedEmployee.id,

              assignedEmployeeName:
                assignedEmployee.name ||
                "",

              assignedEmployeeEmail:
                (
                  assignedEmployee.email ||
                  ""
                )
                  .trim()
                  .toLowerCase(),

              status:
                detectStatusFromRemark(
                  oldRemark
                ),

              callbackStatus:
                "",

              adminNote:
                getColumnValue(
                  rowValues,
                  [
                    "adminnote",
                  ]
                ),

              lastRemark:
                oldRemark ||
                "",

              remarksHistory:
                importedHistory,

              reassignmentHistory:
                [],

              leadEditHistory:
                [],

              createdAt:
                serverTimestamp(),

              updatedAt:
                serverTimestamp(),
            }
          );

          uploadedCount +=
            1;
        }

        setMessage(
          `${uploadedCount} leads uploaded. ${skippedCount} rows skipped. Previous remarks were imported into remark history.`
        );

        await loadPageData();
      } catch (error) {
        console.error(
          "Bulk upload error:",
          error
        );

        setMessage(
          "Unable to upload CSV file."
        );
      } finally {
        setBulkUploading(
          false
        );
      }
    };

  const filteredLeads =
    useMemo(() => {
      const searchValue =
        searchText
          .trim()
          .toLowerCase();

      return leads.filter(
        (lead) => {
          const customerName =
            lead.customerName ||
            lead.name ||
            "";

          const primaryPhone =
            lead.phone ||
            "";

          const alternatePhone =
            lead.alternatePhone ||
            lead.alternateNumber ||
            "";

          const matchesSearch =
            !searchValue ||
            customerName
              .toLowerCase()
              .includes(
                searchValue
              ) ||
            primaryPhone
              .toLowerCase()
              .includes(
                searchValue
              ) ||
            alternatePhone
              .toLowerCase()
              .includes(
                searchValue
              ) ||
            (
              lead.email ||
              ""
            )
              .toLowerCase()
              .includes(
                searchValue
              );

          const matchesEmployee =
            employeeFilter ===
              "All" ||
            lead.assignedEmployeeId ===
              employeeFilter;

          const matchesStatus =
            statusFilter ===
              "All" ||
            (
              lead.status ||
              "Pending"
            ) ===
              statusFilter;

          return (
            matchesSearch &&
            matchesEmployee &&
            matchesStatus
          );
        }
      );
    }, [
      leads,
      searchText,
      employeeFilter,
      statusFilter,
    ]);

  return (
    <div className="manage-employees-page">
      <header className="dashboard-header">

        <div>
          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            Lead Management
          </p>
        </div>

        <button
          type="button"
          className="logout-button"
          onClick={() =>
            navigate(
              "/admin-dashboard"
            )
          }
        >
          Dashboard
        </button>

      </header>

      <main className="dashboard-main">

        <section className="dashboard-welcome">

          <p className="dashboard-label">
            LEAD CONTROL
          </p>

          <h2>
            Manage and assign leads
          </h2>

          <p>
            Add customers, bulk upload old worked data, preserve previous remarks and reassign leads.
          </p>

        </section>

        {message && (
          <div className="attendance-message">
            {message}
          </div>
        )}

        <section className="employee-form-card">

          <div className="section-heading">

            <div>

              <p className="dashboard-label">
                {editingLeadId
                  ? "EDIT LEAD"
                  : "ADD LEAD"}
              </p>

              <h2>
                {editingLeadId
                  ? "Update customer"
                  : "Create new customer lead"}
              </h2>

            </div>

          </div>

          <form
            onSubmit={
              handleSaveLead
            }
          >

            <div className="employee-form-grid">

              <div className="form-group">

                <label>
                  Customer Name *
                </label>

                <input
                  type="text"
                  name="customerName"
                  value={
                    formData.customerName
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Enter customer name"
                />

              </div>

              <div className="form-group">

                <label>
                  Primary Phone Number *
                </label>

                <input
                  type="tel"
                  name="phone"
                  value={
                    formData.phone
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="+91"
                />

              </div>

              <div className="form-group">

                <label>
                  Alternate Number
                </label>

                <input
                  type="tel"
                  name="alternatePhone"
                  value={
                    formData.alternatePhone
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Alternate phone number"
                />

              </div>

              <div className="form-group">

                <label>
                  Email
                </label>

                <input
                  type="email"
                  name="email"
                  value={
                    formData.email
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Customer email"
                />

              </div>

              <div className="form-group">

                <label>
                  Lead Source
                </label>

                <input
                  type="text"
                  name="source"
                  value={
                    formData.source
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Website, WhatsApp, Referral"
                />

              </div>

              <div className="form-group">

                <label>
                  Priority
                </label>

                <select
                  name="priority"
                  value={
                    formData.priority
                  }
                  onChange={
                    handleInputChange
                  }
                >

                  <option value="Low">
                    Low
                  </option>

                  <option value="Normal">
                    Normal
                  </option>

                  <option value="High">
                    High
                  </option>

                  <option value="Urgent">
                    Urgent
                  </option>

                </select>

              </div>

              <div className="form-group">

                <label>
                  Assign Employee *
                </label>

                <select
                  name="assignedEmployeeId"
                  value={
                    formData.assignedEmployeeId
                  }
                  onChange={
                    handleInputChange
                  }
                >

                  <option value="">
                    Select employee
                  </option>

                  {employees
                    .filter(
                      (
                        employeeItem
                      ) =>
                        employeeItem.status !==
                        "Inactive"
                    )
                    .map(
                      (
                        employeeItem
                      ) => (
                        <option
                          key={
                            employeeItem.id
                          }
                          value={
                            employeeItem.id
                          }
                        >
                          {
                            employeeItem.name
                          }
                        </option>
                      )
                    )}

                </select>

              </div>

              <div className="form-group">

                <label>
                  Deadline
                </label>

                <input
                  type="date"
                  name="deadline"
                  value={
                    formData.deadline
                  }
                  onChange={
                    handleInputChange
                  }
                />

              </div>

              <div className="form-group">

                <label>
                  Customer Requirement
                </label>

                <textarea
                  className="crm-textarea"
                  name="requirement"
                  value={
                    formData.requirement
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Customer requirement"
                />

              </div>

              <div className="form-group">

                <label>
                  Private Admin Note
                </label>

                <textarea
                  className="crm-textarea"
                  name="adminNote"
                  value={
                    formData.adminNote
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Employees will not see this note"
                />

              </div>

            </div>

            <div className="employee-form-actions">

              <button
                type="submit"
                className="admin-login-button employee-save-button"
                disabled={
                  saving
                }
              >
                {saving
                  ? "Saving..."
                  : editingLeadId
                    ? "Update Lead"
                    : "Add Lead"}
              </button>

              {editingLeadId && (

                <button
                  type="button"
                  className="secondary-button"
                  onClick={
                    resetForm
                  }
                >
                  Cancel Editing
                </button>

              )}

            </div>

          </form>

        </section>

        <section className="dashboard-section">

          <div className="bulk-upload-placeholder">

            <h3>
              Bulk CSV Upload
            </h3>

            <p>
              Your current sheet format is supported:
            </p>

            <p>
              Customer name | Customer Number | Employee Name | Remark
            </p>

            <p>
              Old remarks are imported into the permanent remark history.
            </p>

            <label
              className="admin-login-button employee-save-button"
              style={{
                display:
                  "inline-flex",

                alignItems:
                  "center",

                justifyContent:
                  "center",

                marginTop:
                  "16px",

                cursor:
                  "pointer",
              }}
            >

              {bulkUploading
                ? "Uploading..."
                : "Choose CSV File"}

              <input
                type="file"
                accept=".csv,text/csv"
                onChange={
                  handleBulkUpload
                }
                disabled={
                  bulkUploading
                }
                style={{
                  display:
                    "none",
                }}
              />

            </label>

          </div>

        </section>

        <section className="dashboard-section">

          <div className="section-heading">

            <div>

              <p className="dashboard-label">
                CUSTOMER LEADS
              </p>

              <h2>
                {filteredLeads.length} records
              </h2>

            </div>

          </div>

          <div className="lead-filter-row">

            <input
              className="employee-search-input"
              type="text"
              placeholder="Search name, phone, alternate phone or email"
              value={
                searchText
              }
              onChange={(
                event
              ) =>
                setSearchText(
                  event.target.value
                )
              }
            />

            <select
              className="lead-filter-select"
              value={
                employeeFilter
              }
              onChange={(
                event
              ) =>
                setEmployeeFilter(
                  event.target.value
                )
              }
            >

              <option value="All">
                All Employees
              </option>

              {employees.map(
                (
                  employeeItem
                ) => (
                  <option
                    key={
                      employeeItem.id
                    }
                    value={
                      employeeItem.id
                    }
                  >
                    {
                      employeeItem.name
                    }
                  </option>
                )
              )}

            </select>

            <select
              className="lead-filter-select"
              value={
                statusFilter
              }
              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >

              <option value="All">
                All Statuses
              </option>

              <option value="Pending">
                Pending
              </option>

              <option value="Interested">
                Interested
              </option>

              <option value="Not Interested">
                Not Interested
              </option>

              <option value="Busy">
                Busy
              </option>

              <option value="Ringing">
                Ringing
              </option>

              <option value="Switched Off">
                Switched Off
              </option>

              <option value="Not Answered">
                Not Answered
              </option>

              <option value="Hang Up">
                Hang Up
              </option>

              <option value="Prospect">
                Prospect
              </option>

              <option value="Converted">
                Converted
              </option>

            </select>

          </div>

          <div
            className="employee-table-wrapper"
            style={{
              marginTop:
                "16px",
            }}
          >

            <table className="employee-table lead-table">

              <thead>

                <tr>
                  <th>
                    Customer
                  </th>

                  <th>
                    Phone
                  </th>

                  <th>
                    Status
                  </th>

                  <th>
                    Priority
                  </th>

                  <th>
                    Employee
                  </th>

                  <th>
                    Previous Remark
                  </th>

                  <th>
                    Deadline
                  </th>

                  <th>
                    Reassign / Churn
                  </th>

                  <th>
                    Actions
                  </th>
                </tr>

              </thead>

              <tbody>

                {loading ? (

                  <tr>
                    <td colSpan="9">
                      Loading leads...
                    </td>
                  </tr>

                ) : filteredLeads.length ===
                  0 ? (

                  <tr>
                    <td colSpan="9">
                      No leads found.
                    </td>
                  </tr>

                ) : (

                  filteredLeads.map(
                    (
                      lead
                    ) => (

                      <tr
                        key={
                          lead.id
                        }
                      >

                        <td>

                          <strong>
                            {lead.customerName ||
                              lead.name ||
                              "Customer"}
                          </strong>

                          <small>
                            {lead.email ||
                              "No email"}
                          </small>

                          {(lead.alternatePhone ||
                            lead.alternateNumber) && (

                            <small>
                              Alt:{" "}
                              {lead.alternatePhone ||
                                lead.alternateNumber}
                            </small>

                          )}

                        </td>

                        <td>
                          {lead.phone ||
                            "-"}
                        </td>

                        <td>

                          <span className="lead-status-badge">
                            {lead.status ||
                              "Pending"}
                          </span>

                        </td>

                        <td>

                          <span
                            className={`priority-badge ${(
                              lead.priority ||
                              "Normal"
                            ).toLowerCase()}`}
                          >
                            {lead.priority ||
                              "Normal"}
                          </span>

                        </td>

                        <td>
                          {lead.assignedEmployeeName ||
                            getEmployeeName(
                              lead.assignedEmployeeId
                            )}
                        </td>

                        <td>
                          {lead.lastRemark ||
                            "-"}
                        </td>

                        <td>
                          {lead.deadline ||
                            "-"}
                        </td>

                        <td>

                          <select
                            className="lead-filter-select"
                            value={
                              lead.assignedEmployeeId ||
                              ""
                            }
                            onChange={(
                              event
                            ) =>
                              handleReassignLead(
                                lead,
                                event.target.value
                              )
                            }
                          >

                            <option value="">
                              Select employee
                            </option>

                            {employees
                              .filter(
                                (
                                  employeeItem
                                ) =>
                                  employeeItem.status !==
                                  "Inactive"
                              )
                              .map(
                                (
                                  employeeItem
                                ) => (
                                  <option
                                    key={
                                      employeeItem.id
                                    }
                                    value={
                                      employeeItem.id
                                    }
                                  >
                                    {
                                      employeeItem.name
                                    }
                                  </option>
                                )
                              )}

                          </select>

                        </td>

                        <td>

                          <div className="table-action-buttons">

                            <button
                              type="button"
                              className="table-button"
                              onClick={() =>
                                handleEditLead(
                                  lead
                                )
                              }
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className="table-button danger"
                              onClick={() =>
                                handleDeleteLead(
                                  lead.id
                                )
                              }
                            >
                              Delete
                            </button>

                          </div>

                        </td>

                      </tr>

                    )
                  )

                )}

              </tbody>

            </table>

          </div>

        </section>

      </main>

    </div>
  );
}

export default ManageLeads;